<template>
  
  <div class="app" ref="refocus" tabindex="-1">
    <Header />
    <main>
      <div class="mx-auto mt-20 max-w-2xl text-center sm:mt-24 px-4">
        <div class="mb-2 md:mb-4">
          <span class="rounded-full mr-2 sm:mr-0 mb-2 sm:mb-0 bg-[#8293fa] px-2 py-0 text-xs inline-block font-semibold leading-6 text-white no-underline">
            Error
          </span>
        </div>
        <h1 class="text-h1">{{ message }}</h1>
      </div>
      <div class="mx-auto my-16 flow-root max-w-lg sm:my-20 px-4">
        <h2 class="sr-only">Popular pages</h2>
        <ul role="list" class="-mt-6">
          <li class="relative flex gap-x-6 py-6 border-b-2 border-[#e8e5de]">
            <div class="flex h-10 w-10 flex-none items-center justify-center rounded-lg ring-2 ring-[#e8e5de]">
              <Icon name="iconoir:security-pass" color="#6073EE" class="h-6 w-6 text-[#5a6ded]" />
            </div>
            <div class="flex-auto">
              <h3 class="text-md font-semibold leading-6 md:leading-6 text-gray-900 md:text-md lg:text-lg">
                <NuxtLink :to="$localePath('/features')">
                  <span class="absolute inset-0" />
                  Features
                </NuxtLink>
              </h3>
              <p class="text-sm leading-6">Anonymous reporting, surveys, case management & analytics</p>
            </div>
            <div class="flex-none self-center">
              ->
            </div>
          </li>
          <li class="relative flex gap-x-6 py-6 border-b-2 border-[#e8e5de]">
            <div class="flex h-10 w-10 flex-none items-center justify-center rounded-lg ring-2 ring-[#e8e5de]">
              <Icon name="iconoir:design-nib" color="#6073EE" class="h-6 w-6 text-[#5a6ded]" />
            </div>
            <div class="flex-auto">
              <h3 class="text-md font-semibold leading-6 md:leading-6 text-gray-900 md:text-md lg:text-lg">
                <NuxtLink :to="$localePath('/blog')">
                  <span class="absolute inset-0" />
                  Blog
                </NuxtLink>
              </h3>
              <p class="text-sm leading-6">View the latest insights into workplace reporting, surveys and analytics</p>
            </div>
            <div class="flex-none self-center">
              ->
            </div>
          </li>
          <li class="relative flex gap-x-6 py-6 border-b-2 border-[#e8e5de]">
            <div class="flex h-10 w-10 flex-none items-center justify-center rounded-lg ring-2 ring-[#E4E2DF]">
              <Icon name="iconoir:chat-bubble" color="#6073EE" class="h-6 w-6 text-[#5a6ded]" />
            </div>
            <div class="flex-auto">
              <h3 class="text-md font-semibold leading-6 md:leading-6 text-gray-900 md:text-md lg:text-lg">
                <NuxtLink :to="$localePath('/contact')">
                  <span class="absolute inset-0" />
                  Contact us
                </NuxtLink>
              </h3>
              <p class="text-sm leading-6">Speak to us about how we can improve your reporting channels</p>
            </div>
            <div class="flex-none self-center">
              ->
            </div>
          </li>
          <li class="relative flex gap-x-6 py-6 border-b-2 border-[#e8e5de]">
            <div class="flex h-10 w-10 flex-none items-center justify-center rounded-lg ring-2 ring-[#E4E2DF]">
              <Icon name="iconoir:app-window" color="#6073EE" class="h-6 w-6 text-[#5a6ded]" />
            </div>
            <div class="flex-auto">
              <h3 class="text-md font-semibold leading-6 md:leading-6 text-gray-900 md:text-md lg:text-lg">
                <NuxtLink :to="$localePath('/contact')">
                  <span class="absolute inset-0" />
                  Book a demo
                </NuxtLink>
              </h3>
              <p class="text-sm leading-6">Book a demonstration of our anonymous reporting platform and app.</p>
            </div>
            <div class="flex-none self-center">
              ->
            </div>
          </li>
        </ul>
      </div>


      <FadeIn>
        <Container>
          <div class="mx-auto max-w-3xl pb-8 sm:pb-12 lg:pb-16 lg:pt-16">
            <div class="text-center">     
              <FadeIn>
                <h1 class="text-3xl leading-[1.25] sm:text-4xl sm:leading-[1.2] md:text-5xl md:leading-[1.12] font-bold tracking-tight text-gray-900 lora">
                  See how Elker works
                </h1>
                <div class="text-subtitle">
                  Watch a short introduction to the platform by clicking the play button below
                </div>
              </FadeIn>
            </div>
          </div>
          <div 
          class="max-w-[1080px] mx-auto rounded-[20px] md:rounded-[35px] mb-16 lg:mb-24 overflow-hidden"
          >
            <VideoPlayer
              thumbnail="https://cdn.elker.com/meet-elker/thumbnail_bba26ec6.jpg"
              :videoSources="[
                { quality: '1080p', url: 'https://cdn.elker.com/meet-elker/play_1080p.mp4' },
                { quality: '720p', url: 'https://cdn.elker.com/meet-elker/play_720p.mp4' },
                { quality: '480p', url: 'https://cdn.elker.com/meet-elker/play_480p.mp4' }
              ]"
              captions="https://cdn.elker.com/meet-elker/captions/en.vtt"
            />
          </div>
        </Container>
      </FadeIn>

    </main>
    <Footer />
  </div>

</template>

<script setup>
const error = useError()

const statusCode = computed(() => error.value?.statusCode || 404)
const theUrl = computed(() => error.value?.url || '/')

const message = computed(() => {
  if (statusCode.value === 404) {
    return 'Page not found'
  } else if (statusCode.value === 500) {
    return 'Internal server error'
  } else {
    return 'An unexpected error occurred'
  }
})

const seo = computed(() => {
  return {
    metaTitle: statusCode.value + ' | ' + theUrl.value,
    metaDescription: message.value,
    metaImage: null
  }
})

useHead(seo)
</script>